/**
 * The lens, on the stage where it applies, as a SENTENCE: "Showing items
 * tagged [content: user complaint, pricing ▾] and [product function: data
 * analytics ▾] · 18 items". Each tag picked in the Tags drawer is a TERM, and
 * the terms all hold ("and"), two of one category too. A term's dropdown
 * ticks more of its category's values into it, as alternatives ("or"). The
 * sentence is the logic, so nobody has to know the words AND and OR: they
 * read what they will get.
 *
 * A check or an author narrowing the view is a chip after the sentence, as
 * before. A filter left selected by accident is obvious here; a dig that says
 * "within …" points at exactly this.
 *
 * A TERM CAN BE "IS NOT": "and not [product function: crm ▾]", switched in
 * its dropdown. It keeps items sorted on the category that carry none of its
 * values; an item never sorted on it is left out, since nothing says it is not
 * a CRM.
 *
 * FOLLOWING A LEAD, its terms are held: ticked and locked, since an
 * alternative would widen past the lead, and "clear" leaves them, because the
 * way out of a lead is the lead bar's Back.
 * "Follow this lead" appears once the lens narrows past what the room follows.
 */
import { useEffect, useRef, useState } from 'react';
import type { BoardroomLead, BoardroomPersona } from '../../../types';
import { VERIFIED_OPTIONS, setTermNegated, setTermValues, tagTerms, type View } from '../filters';
import { narrowsPast, viewForLead } from '../leads/leadLens';

type Values = Record<string, { value: string; count: number }[]>;

const dimLabel = (d: string) => d.toLowerCase().replace(/_/g, ' ');

/** Past this many values a term's dropdown gets a box to find one. */
const FIND_AFTER = 12;

function Term({
  dimension,
  picked,
  values,
  locked,
  negated,
  onToggle,
  onNegate,
  onRemove,
}: {
  dimension: string;
  picked: string[];
  values: { value: string; count: number }[];
  /** A term of the lead being followed: nothing ticks in or out. */
  locked: boolean;
  /** "is not": none of the picked values, among items sorted on the category. */
  negated: boolean;
  onToggle: (value: string) => void;
  onNegate: (negated: boolean) => void;
  onRemove: (() => void) | null;
}) {
  const [open, setOpen] = useState(false);
  const [find, setFind] = useState('');
  const box = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);
  // Picked first, so a long category never hides what is ticked; then by count.
  const all = [
    ...picked.map(v => values.find(x => x.value === v) ?? { value: v, count: 0 }),
    ...values.filter(x => !picked.includes(x.value)),
  ];
  const shown = find.trim() ? all.filter(x => x.value.toLowerCase().includes(find.trim().toLowerCase())) : all;
  return (
    <span ref={box} className="relative inline-block">
      <span
        className={`inline-flex items-center rounded-full ring-1 ${
          locked
            ? 'bg-indigo-500 text-white ring-indigo-500 dark:bg-indigo-400'
            : negated
              ? 'bg-amber-100 text-amber-900 ring-amber-300 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-700'
              : 'bg-sky-100 text-sky-900 ring-sky-300 dark:bg-sky-900/50 dark:text-sky-100 dark:ring-sky-700'
        }`}
        data-testid={`lens-chip-${dimension}`}
      >
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          className={`inline-flex items-center gap-1 rounded-full py-0.5 ${onRemove ? 'pl-2 pr-1' : 'px-2'} ${
            locked ? '' : 'hover:bg-sky-200 dark:hover:bg-sky-800/60'
          }`}
          title={
            locked
              ? 'Part of the lead you are following'
              : negated
                ? 'Items sorted on this category that are none of these'
                : 'Tick values in or out: any of them'
          }
          data-testid={`lens-term-${dimension}`}
        >
          <span className="opacity-75">{dimLabel(dimension)}:</span> {picked.join(', ')} <span aria-hidden>▾</span>
        </button>
        {/* THE WHOLE TERM OFF IN ONE CLICK, as every other chip here. It was
            only "Remove this term" at the bottom of the dropdown. */}
        {onRemove && (
          <button
            type="button"
            aria-label={`Remove ${dimLabel(dimension)}: ${picked.join(', ')}`}
            onClick={onRemove}
            className="icon-btn pr-1.5 py-0.5 text-sky-500 hover:text-red-500"
            data-testid={`lens-term-clear-${dimension}`}
          >
            ×
          </button>
        )}
      </span>
      {open && (
        <div
          role="dialog"
          aria-label={dimLabel(dimension)}
          className="absolute left-0 top-full z-30 mt-1 w-64 max-w-[85vw] rounded-lg border border-gray-200 bg-white p-2 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-800"
          data-testid={`lens-term-menu-${dimension}`}
        >
          {/* IS · IS NOT: a term keeps items that are any of its values, or
              items sorted on the category that are none of them. */}
          <div role="radiogroup" aria-label="is or is not" className="mb-1.5 inline-flex overflow-hidden rounded border border-gray-300 dark:border-gray-600">
            {[
              { on: !negated, label: 'is', value: false },
              { on: negated, label: 'is not', value: true },
            ].map(o => (
              <button
                key={o.label}
                type="button"
                role="radio"
                aria-checked={o.on}
                disabled={locked}
                onClick={() => onNegate(o.value)}
                className={`px-2 py-0.5 ${o.on ? 'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-gray-100' : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                data-testid={`lens-term-${o.value ? 'not' : 'is'}-${dimension}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {dimLabel(dimension)} · {negated ? 'none of' : 'any of'}
          </div>
          {negated && (
            <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">
              Only items sorted on {dimLabel(dimension)}: one nothing is known about is left out.
            </p>
          )}
          {all.length > FIND_AFTER && (
            <input
              autoFocus
              value={find}
              onChange={e => setFind(e.target.value)}
              placeholder="Find a value"
              className="mb-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-900"
            />
          )}
          <ul className="max-h-64 overflow-y-auto">
            {shown.map(x => {
              const on = picked.includes(x.value);
              const fixed = locked;
              return (
                <li key={x.value}>
                  <label
                    className={`flex items-center gap-2 rounded px-1 py-0.5 ${fixed ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    title={fixed ? 'Held by the lead you are following' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={fixed}
                      onChange={() => onToggle(x.value)}
                      data-testid={`lens-value-${dimension}:${x.value}`}
                    />
                    <span className="flex-1 truncate text-gray-800 dark:text-gray-100">{x.value}</span>
                    <span className="tabular-nums text-gray-400">{x.count.toLocaleString()}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          {onRemove && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onRemove();
              }}
              className="mt-1 w-full rounded px-1 py-0.5 text-left text-gray-600 hover:bg-gray-50 hover:text-red-600 dark:text-gray-300 dark:hover:bg-gray-700"
              data-testid={`lens-term-remove-${dimension}`}
            >
              Remove this term
            </button>
          )}
        </div>
      )}
    </span>
  );
}

export default function LensBar({
  view,
  onView,
  personas,
  lead,
  onFollow,
  embedded = false,
  termFacets = null,
  roomTagFacets = {},
  total = null,
}: {
  view: View;
  onView: (next: View) => void;
  personas: Map<number, BoardroomPersona>;
  /** The lead the room follows: its values are held. */
  lead?: BoardroomLead | null;
  /** Open "Follow this lead" for the lens as it is. */
  onFollow?: () => void;
  /** Drawn as the bottom of the box it shares with the lead bar. */
  embedded?: boolean;
  /** Each term's category values, counted under the OTHER terms. */
  termFacets?: { key: string; values: { value: string; count: number }[] }[] | null;
  /** The room's values, for a category the counts do not cover yet. */
  roomTagFacets?: Values;
  /** How many items the lens leaves. */
  total?: number | null;
}) {
  const held = lead?.lens;
  const heldTags = held?.tags ?? [];
  const terms = tagTerms(view.tags);
  const chips: { key: string; label: string; remove: (() => void) | null }[] = [];
  if (view.verified) {
    chips.push({
      key: 'verified',
      label: VERIFIED_OPTIONS.find(o => o.key === view.verified)?.label ?? view.verified.toLowerCase(),
      remove: held?.verified ? null : () => onView({ ...view, verified: null }),
    });
  }
  if (view.by) {
    const who = view.by === 'human' ? 'you' : personas.get(Number(view.by))?.name ?? `persona #${view.by}`;
    chips.push({ key: 'by', label: `by ${who}`, remove: held?.by ? null : () => onView({ ...view, by: null }) });
  }
  if (terms.length === 0 && chips.length === 0) return null;
  const extra = narrowsPast(view, lead);
  return (
    <div
      className={`shrink-0 flex flex-wrap items-center gap-1.5 px-2 py-1 text-xs ${
        embedded
          ? 'border-t border-indigo-200 dark:border-indigo-800 bg-white/70 dark:bg-gray-800/40'
          : 'rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-900/10'
      }`}
      data-testid="lens-bar"
    >
      <span className="text-gray-600 dark:text-gray-300">{terms.length ? 'Showing items tagged' : 'Showing'}</span>
      {terms.map((t, i) => {
        const locked = heldTags.includes(t.key);
        const joiner = [i > 0 ? 'and' : '', t.negated ? 'not' : ''].filter(Boolean).join(' ');
        return (
          <span key={t.key} className="inline-flex items-center gap-1.5">
            {joiner && <span className="text-gray-500 dark:text-gray-400">{joiner}</span>}
            <Term
              dimension={t.dimension}
              picked={t.values}
              values={termFacets?.find(f => f.key === t.key)?.values ?? roomTagFacets[t.dimension] ?? []}
              locked={locked}
              negated={t.negated}
              onNegate={negated => onView({ ...view, tags: setTermNegated(view.tags, t.key, negated) })}
              onToggle={v =>
                onView({
                  ...view,
                  tags: setTermValues(
                    view.tags,
                    t.key,
                    t.values.includes(v) ? t.values.filter(x => x !== v) : [...t.values, v],
                  ),
                })
              }
              onRemove={locked ? null : () => onView({ ...view, tags: view.tags.filter(x => x !== t.key) })}
            />
          </span>
        );
      })}
      {chips.map(c =>
        c.remove ? (
          <span key={c.key} className="inline-flex items-center rounded-full bg-sky-100 text-sky-900 ring-1 ring-sky-300 dark:bg-sky-900/50 dark:text-sky-100 dark:ring-sky-700">
            <span className="pl-2 pr-1 py-0.5">{c.label}</span>
            <button type="button" aria-label={`Remove ${c.label}`} onClick={c.remove} className="icon-btn pr-1.5 py-0.5 text-sky-500 hover:text-red-500">
              ×
            </button>
          </span>
        ) : (
          <span
            key={c.key}
            title="Part of the lead you are following. Use Back on the lead bar to leave it."
            className="inline-flex items-center rounded-full bg-indigo-500 px-2 py-0.5 text-white dark:bg-indigo-400"
            data-testid="lens-held-chip"
          >
            {c.label}
          </span>
        ),
      )}
      {total != null && (
        <span className="text-gray-500 dark:text-gray-400" data-testid="lens-total">
          · {total.toLocaleString()} item{total === 1 ? '' : 's'}
        </span>
      )}
      {extra && (
        <button
          type="button"
          onClick={() => onView(lead ? viewForLead(view, lead) : { ...view, tags: [], verified: null, by: null })}
          className="text-indigo-600 dark:text-indigo-300 hover:underline"
        >
          clear
        </button>
      )}
      {extra && onFollow && (
        <button
          type="button"
          onClick={onFollow}
          title="Have the room follow this part of it: digs stay inside it until you go back"
          className="px-2 py-0.5 rounded border border-indigo-300 dark:border-indigo-700 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/30"
          data-testid="follow-this-lead"
        >
          {lead ? 'Follow this lead inside it' : 'Follow this lead'}
        </button>
      )}
    </div>
  );
}
